import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import { usePacientes } from '../hooks/usePacientes';
import {
  getFoodDiaryEntry,
  addFoodToMeal,
  removeFoodFromMeal,
  updateWaterIntake,
  getFoodDiaryHistory,
  getFoodDiaryStats,
  formatDate,
  MealType,
  FoodDiaryEntry,
} from '../services/foodDiaryService';
import {
  searchFoods,
  getFoodById,
  createCustomFood,
  FoodItem,
} from '../services/foodDatabaseService';
import './Pages.css';

const MEAL_LABELS: Record<MealType, string> = {
  breakfast: 'Café da Manhã',
  morningSnack: 'Lanche da Manhã',
  lunch: 'Almoço',
  afternoonSnack: 'Lanche da Tarde',
  dinner: 'Jantar',
  eveningSnack: 'Lanche da Noite',
};

export const FoodDiary: React.FC = () => {
  const { user } = useAuth();
  const { pacientes, loading: pacientesLoading, error: pacientesError } = usePacientes();
  
  const [selectedPatient, setSelectedPatient] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(formatDate(new Date()));
  const [diaryEntry, setDiaryEntry] = useState<FoodDiaryEntry | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Modal de adicionar alimento
  const [isAddFoodModalOpen, setIsAddFoodModalOpen] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState<MealType>('breakfast');
  const [foodSearchTerm, setFoodSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<FoodItem[]>([]);
  const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null);
  const [foodQuantity, setFoodQuantity] = useState<number>(100);
  const [foodUnit, setFoodUnit] = useState<FoodItem['servingUnit']>('g');
  const [foodNotes, setFoodNotes] = useState('');
  
  // Modal de criar alimento customizado
  const [isCreateFoodModalOpen, setIsCreateFoodModalOpen] = useState(false);
  const [newFood, setNewFood] = useState<Partial<FoodItem>>({
    name: '',
    category: 'other',
    servingSize: 100,
    servingUnit: 'g',
    nutritionalInfo: {
      calories: 0,
      protein: 0,
      carbs: 0,
      fats: 0,
    },
  });
  
  // Estatísticas
  const [stats, setStats] = useState<any>(null);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    if (selectedPatient && selectedDate) {
      loadDiaryEntry();
    }
  }, [selectedPatient, selectedDate]);

  useEffect(() => {
    if (foodSearchTerm.length >= 2) {
      searchFoodsDebounced();
    } else {
      setSearchResults([]);
    }
  }, [foodSearchTerm]);

  useEffect(() => {
    if (selectedPatient) {
      loadStats();
    }
  }, [selectedPatient]);

  const loadDiaryEntry = async () => {
    if (!selectedPatient) {
      console.log('FoodDiary: Nenhum paciente selecionado');
      return;
    }
    setLoading(true);
    try {
      console.log(`FoodDiary: Buscando diário para paciente ${selectedPatient} na data ${selectedDate}`);
      const entry = await getFoodDiaryEntry(selectedPatient, selectedDate);
      console.log('FoodDiary: Entrada encontrada:', entry ? 'Sim' : 'Não');
      setDiaryEntry(entry);
    } catch (error) {
      console.error('Erro ao carregar diário:', error);
      alert('Erro ao carregar o diário alimentar. Verifique o console para mais detalhes.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!selectedPatient) return;
    try {
      const statsData = await getFoodDiaryStats(selectedPatient, 7);
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const searchFoodsDebounced = async () => {
    try {
      const results = await searchFoods(foodSearchTerm);
      setSearchResults(results.slice(0, 10));
    } catch (error) {
      console.error('Erro ao buscar alimentos:', error);
    }
  };

  const handleAddFood = async () => {
    if (!selectedFood || !selectedPatient) return;
    
    try {
      await addFoodToMeal(
        selectedPatient,
        selectedDate,
        selectedMealType,
        selectedFood,
        foodQuantity,
        foodUnit,
        foodNotes
      );
      
      await loadDiaryEntry();
      setIsAddFoodModalOpen(false);
      setSelectedFood(null);
      setFoodQuantity(100);
      setFoodUnit('g');
      setFoodNotes('');
      setFoodSearchTerm('');
    } catch (error) {
      console.error('Erro ao adicionar alimento:', error);
      alert('Erro ao adicionar alimento. Tente novamente.');
    }
  };

  const handleRemoveFood = async (mealType: MealType, entryId: string) => {
    if (!selectedPatient) return;
    
    try {
      await removeFoodFromMeal(selectedPatient, selectedDate, mealType, entryId);
      await loadDiaryEntry();
    } catch (error) {
      console.error('Erro ao remover alimento:', error);
      alert('Erro ao remover alimento. Tente novamente.');
    }
  };

  const handleUpdateWater = async (waterIntake: number) => {
    if (!selectedPatient) return;
    
    try {
      await updateWaterIntake(selectedPatient, selectedDate, waterIntake);
      await loadDiaryEntry();
    } catch (error) {
      console.error('Erro ao atualizar água:', error);
      alert('Erro ao atualizar ingestão de água. Tente novamente.');
    }
  };

  const handleCreateCustomFood = async () => {
    if (!user?.uid || !newFood.name || !newFood.nutritionalInfo) return;
    
    try {
      await createCustomFood(user.uid, newFood as Omit<FoodItem, 'id' | 'createdAt' | 'customFood' | 'verified'>);
      setIsCreateFoodModalOpen(false);
      setNewFood({
        name: '',
        category: 'other',
        servingSize: 100,
        servingUnit: 'g',
        nutritionalInfo: {
          calories: 0,
          protein: 0,
          carbs: 0,
          fats: 0,
        },
      });
      alert('Alimento criado com sucesso!');
    } catch (error) {
      console.error('Erro ao criar alimento:', error);
      alert('Erro ao criar alimento. Tente novamente.');
    }
  };

  const getPatientName = (patientId: string): string => {
    const patient = pacientes.find(p => p.id === patientId);
    return patient?.nome || 'Paciente';
  };

  const changeDate = (days: number) => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + days);
    setSelectedDate(formatDate(currentDate));
  };

  const getMealTotal = (mealType: MealType) => {
    if (!diaryEntry) return { calories: 0, protein: 0, carbs: 0, fats: 0 };
    
    return diaryEntry.meals[mealType].reduce((acc, entry) => ({
      calories: acc.calories + entry.nutritionalInfo.calories,
      protein: acc.protein + entry.nutritionalInfo.protein,
      carbs: acc.carbs + entry.nutritionalInfo.carbs,
      fats: acc.fats + entry.nutritionalInfo.fats,
    }), { calories: 0, protein: 0, carbs: 0, fats: 0 });
  };

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Diário Alimentar</h2>
          <p>Registro detalhado de alimentação diária dos pacientes.</p>
        </div>
        <div className="ns-page-header-actions">
          <Button variant="secondary" onClick={() => setShowStats(!showStats)}>
            📊 Estatísticas
          </Button>
        </div>
      </div>

      <Card title="Seleção de Paciente e Data">
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <label className="ns-field" style={{ flex: 1, minWidth: '200px' }}>
            <span>Paciente</span>
            {pacientesLoading ? (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--page-text-muted)' }}>
                Carregando pacientes...
              </div>
            ) : pacientesError ? (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--theme-danger)' }}>
                Erro ao carregar pacientes: {pacientesError}
              </div>
            ) : pacientes.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--page-text-muted)' }}>
                Nenhum paciente encontrado. Adicione pacientes primeiro.
              </div>
            ) : (
              <select
                className="ns-input"
                value={selectedPatient}
                onChange={(e) => setSelectedPatient(e.target.value)}
              >
                <option value="">Selecione um paciente</option>
                {pacientes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            )}
          </label>
          <label className="ns-field" style={{ minWidth: '150px' }}>
            <span>Data</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button variant="secondary" onClick={() => changeDate(-1)} style={{ padding: '4px 8px' }}>
                ←
              </Button>
              <input
                type="date"
                className="ns-input"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ flex: 1 }}
              />
              <Button variant="secondary" onClick={() => changeDate(1)} style={{ padding: '4px 8px' }}>
                →
              </Button>
            </div>
          </label>
        </div>
      </Card>

      {showStats && stats && (
        <Card title="Estatísticas (Últimos 7 dias)" style={{ marginTop: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <div style={{ textAlign: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--theme-link)' }}>{stats.averageCalories}</div>
              <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>Calorias/dia</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--theme-danger)' }}>{stats.averageProtein}g</div>
              <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>Proteína/dia</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#3b82f6' }}>{stats.averageCarbs}g</div>
              <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>Carboidratos/dia</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#f59e0b' }}>{stats.averageFats}g</div>
              <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>Gorduras/dia</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#06b6d4' }}>{stats.averageWater}ml</div>
              <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>Água/dia</div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 600, color: '#10b981' }}>{stats.totalDays}</div>
              <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>Dias registrados</div>
            </div>
          </div>
        </Card>
      )}

      {loading && (
        <Card style={{ marginTop: '16px' }}>
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--page-text-muted)' }}>
            Carregando diário alimentar...
          </div>
        </Card>
      )}

      {selectedPatient && !loading && (
        <>
          {/* Resumo Diário */}
          {diaryEntry ? (
            <Card title="Resumo Nutricional do Dia" style={{ marginTop: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                <div style={{ textAlign: 'center', padding: '16px', background: '#eff6ff', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 600, color: 'var(--theme-link)' }}>
                    {diaryEntry.dailySummary.totalCalories}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px' }}>Calorias</div>
                </div>
                <div style={{ textAlign: 'center', padding: '16px', background: '#fef2f2', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 600, color: 'var(--theme-danger)' }}>
                    {diaryEntry.dailySummary.totalProtein}g
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px' }}>Proteínas</div>
                </div>
                <div style={{ textAlign: 'center', padding: '16px', background: '#eff6ff', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 600, color: '#3b82f6' }}>
                    {diaryEntry.dailySummary.totalCarbs}g
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px' }}>Carboidratos</div>
                </div>
                <div style={{ textAlign: 'center', padding: '16px', background: '#fffbeb', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 600, color: '#f59e0b' }}>
                    {diaryEntry.dailySummary.totalFats}g
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px' }}>Gorduras</div>
                </div>
                <div style={{ textAlign: 'center', padding: '16px', background: '#ecfeff', borderRadius: '8px' }}>
                  <div style={{ fontSize: '28px', fontWeight: 600, color: '#06b6d4' }}>
                    {diaryEntry.dailySummary.waterIntake}ml
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px' }}>Água</div>
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <label className="ns-field">
                  <span>Ingestão de Água (ml)</span>
                  <input
                    type="number"
                    className="ns-input"
                    value={diaryEntry.dailySummary.waterIntake}
                    onChange={(e) => handleUpdateWater(Number(e.target.value))}
                    min="0"
                    step="50"
                  />
                </label>
              </div>
            </Card>
          ) : (
            <Card title="Resumo Nutricional do Dia" style={{ marginTop: '16px' }}>
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--page-text-muted)' }}>
                Nenhum registro encontrado para esta data. Adicione alimentos para começar.
              </div>
            </Card>
          )}

          {/* Refeições */}
          {Object.entries(MEAL_LABELS).map(([mealType, label]) => {
            const mealEntries = diaryEntry?.meals[mealType as MealType] || [];
            const mealTotal = getMealTotal(mealType as MealType);
            
            return (
              <Card
                key={mealType}
                title={label}
                actions={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSelectedMealType(mealType as MealType);
                      setIsAddFoodModalOpen(true);
                    }}
                    style={{ fontSize: '12px', padding: '4px 8px' }}
                  >
                    + Adicionar
                  </Button>
                }
                style={{ marginTop: '16px' }}
              >
                {mealEntries.length === 0 ? (
                  <p style={{ color: 'var(--page-text-muted)', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                    Nenhum alimento registrado
                  </p>
                ) : (
                  <>
                    <div style={{ marginBottom: '12px', padding: '8px', background: '#f8fafc', borderRadius: '6px', fontSize: '12px' }}>
                      <strong>Total da refeição:</strong> {mealTotal.calories} kcal | 
                      P: {mealTotal.protein.toFixed(1)}g | 
                      C: {mealTotal.carbs.toFixed(1)}g | 
                      G: {mealTotal.fats.toFixed(1)}g
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {mealEntries.map((entry) => (
                        <div
                          key={entry.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px',
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                              {entry.foodName}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>
                              {entry.quantity} {entry.unit} • {entry.nutritionalInfo.calories} kcal | 
                              P: {entry.nutritionalInfo.protein}g | 
                              C: {entry.nutritionalInfo.carbs}g | 
                              G: {entry.nutritionalInfo.fats}g
                            </div>
                            {entry.notes && (
                              <div style={{ fontSize: '11px', color: 'var(--page-text-muted)', marginTop: '4px', fontStyle: 'italic' }}>
                                {entry.notes}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="secondary"
                            onClick={() => handleRemoveFood(mealType as MealType, entry.id)}
                            style={{ fontSize: '11px', padding: '4px 8px', marginLeft: '12px' }}
                          >
                            Remover
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Card>
            );
          })}
        </>
      )}

      {/* Modal de Adicionar Alimento */}
      <Modal
        isOpen={isAddFoodModalOpen}
        onClose={() => {
          setIsAddFoodModalOpen(false);
          setSelectedFood(null);
          setFoodSearchTerm('');
        }}
        title={`Adicionar Alimento - ${MEAL_LABELS[selectedMealType]}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsAddFoodModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddFood}
              disabled={!selectedFood}
            >
              Adicionar
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label className="ns-field">
              <span>Buscar Alimento</span>
              <input
                type="text"
                className="ns-input"
                value={foodSearchTerm}
                onChange={(e) => setFoodSearchTerm(e.target.value)}
                placeholder="Digite o nome do alimento..."
              />
            </label>
            {searchResults.length > 0 && (
              <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                {searchResults.map((food) => (
                  <div
                    key={food.id}
                    onClick={() => {
                      setSelectedFood(food);
                      setFoodQuantity(food.servingSize);
                      setFoodUnit(food.servingUnit);
                      setFoodSearchTerm(food.name);
                    }}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      background: selectedFood?.id === food.id ? '#eff6ff' : '#ffffff',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ fontWeight: 600, fontSize: '13px' }}>{food.name}</div>
                      {food.fromTaco && (
                        <span
                          style={{
                            fontSize: '9px',
                            padding: '2px 6px',
                            background: '#10b981',
                            color: 'white',
                            borderRadius: '4px',
                            fontWeight: 600,
                          }}
                          title="Dados verificados da tabela TACO"
                        >
                          TACO
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--page-text-muted)' }}>
                      {food.nutritionalInfo.calories} kcal | 
                      P: {food.nutritionalInfo.protein}g | 
                      C: {food.nutritionalInfo.carbs}g | 
                      G: {food.nutritionalInfo.fats}g
                      {food.nutritionalInfo.fiber !== undefined && ` | Fibra: ${food.nutritionalInfo.fiber}g`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedFood && (
            <>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600 }}>Alimento selecionado: {selectedFood.name}</div>
                  {selectedFood.fromTaco && (
                    <span
                      style={{
                        fontSize: '10px',
                        padding: '3px 8px',
                        background: '#10b981',
                        color: 'white',
                        borderRadius: '4px',
                        fontWeight: 600,
                      }}
                      title="Dados verificados da tabela TACO"
                    >
                      ✓ TACO
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginBottom: '8px' }}>
                  Por {selectedFood.servingSize} {selectedFood.servingUnit}: 
                  {selectedFood.nutritionalInfo.calories} kcal | 
                  P: {selectedFood.nutritionalInfo.protein}g | 
                  C: {selectedFood.nutritionalInfo.carbs}g | 
                  G: {selectedFood.nutritionalInfo.fats}g
                  {selectedFood.nutritionalInfo.fiber !== undefined && ` | Fibra: ${selectedFood.nutritionalInfo.fiber}g`}
                </div>
                {selectedFood.fromTaco && selectedFood.nomeCientifico && (
                  <div style={{ fontSize: '11px', color: 'var(--page-text-muted)', fontStyle: 'italic', marginTop: '4px' }}>
                    {selectedFood.nomeCientifico}
                  </div>
                )}
                {/* Informações nutricionais adicionais quando disponíveis */}
                {(selectedFood.nutritionalInfo.calcium !== undefined ||
                  selectedFood.nutritionalInfo.iron !== undefined ||
                  selectedFood.nutritionalInfo.vitaminC !== undefined ||
                  selectedFood.nutritionalInfo.vitaminA !== undefined) && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: 'var(--page-text-muted)' }}>
                      Informações Nutricionais Adicionais:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '6px', fontSize: '10px', color: 'var(--page-text-muted)' }}>
                      {selectedFood.nutritionalInfo.calcium !== undefined && (
                        <div>Ca: {selectedFood.nutritionalInfo.calcium}mg</div>
                      )}
                      {selectedFood.nutritionalInfo.iron !== undefined && (
                        <div>Fe: {selectedFood.nutritionalInfo.iron}mg</div>
                      )}
                      {selectedFood.nutritionalInfo.potassium !== undefined && (
                        <div>K: {selectedFood.nutritionalInfo.potassium}mg</div>
                      )}
                      {selectedFood.nutritionalInfo.vitaminA !== undefined && (
                        <div>Vit. A: {selectedFood.nutritionalInfo.vitaminA}μg</div>
                      )}
                      {selectedFood.nutritionalInfo.vitaminC !== undefined && (
                        <div>Vit. C: {selectedFood.nutritionalInfo.vitaminC}mg</div>
                      )}
                      {selectedFood.nutritionalInfo.vitaminD !== undefined && (
                        <div>Vit. D: {selectedFood.nutritionalInfo.vitaminD}μg</div>
                      )}
                      {selectedFood.nutritionalInfo.magnesium !== undefined && (
                        <div>Mg: {selectedFood.nutritionalInfo.magnesium}mg</div>
                      )}
                      {selectedFood.nutritionalInfo.zinc !== undefined && (
                        <div>Zn: {selectedFood.nutritionalInfo.zinc}mg</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <label className="ns-field">
                  <span>Quantidade</span>
                  <input
                    type="number"
                    className="ns-input"
                    value={foodQuantity}
                    onChange={(e) => setFoodQuantity(Number(e.target.value))}
                    min="0"
                    step="0.1"
                  />
                </label>
                <label className="ns-field">
                  <span>Unidade</span>
                  <select
                    className="ns-input"
                    value={foodUnit}
                    onChange={(e) => setFoodUnit(e.target.value as FoodItem['servingUnit'])}
                  >
                    <option value="g">g</option>
                    <option value="ml">ml</option>
                    <option value="unit">Unidade</option>
                    <option value="cup">Xícara</option>
                    <option value="tablespoon">Colher de Sopa</option>
                    <option value="teaspoon">Colher de Chá</option>
                  </select>
                </label>
              </div>

              <label className="ns-field">
                <span>Observações (opcional)</span>
                <textarea
                  className="ns-textarea"
                  rows={2}
                  value={foodNotes}
                  onChange={(e) => setFoodNotes(e.target.value)}
                  placeholder="Ex: grelhado, sem óleo, etc."
                />
              </label>
            </>
          )}

          <div style={{ padding: '12px', background: '#fef3c7', borderRadius: '8px', fontSize: '12px' }}>
            <strong>💡 Dica:</strong> Não encontrou o alimento?{' '}
            <button
              onClick={() => {
                setIsAddFoodModalOpen(false);
                setIsCreateFoodModalOpen(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--theme-link)',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Crie um alimento personalizado
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de Criar Alimento Customizado */}
      <Modal
        isOpen={isCreateFoodModalOpen}
        onClose={() => setIsCreateFoodModalOpen(false)}
        title="Criar Alimento Personalizado"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsCreateFoodModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateCustomFood} disabled={!newFood.name}>
              Criar
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <label className="ns-field">
            <span>Nome do Alimento</span>
            <input
              type="text"
              className="ns-input"
              value={newFood.name || ''}
              onChange={(e) => setNewFood({ ...newFood, name: e.target.value })}
              placeholder="Ex: Bife de frango grelhado"
            />
          </label>

          <label className="ns-field">
            <span>Categoria</span>
            <select
              className="ns-input"
              value={newFood.category || 'other'}
              onChange={(e) => setNewFood({ ...newFood, category: e.target.value as FoodItem['category'] })}
            >
              <option value="protein">Proteína</option>
              <option value="carbs">Carboidrato</option>
              <option value="fats">Gordura</option>
              <option value="vegetables">Vegetal</option>
              <option value="fruits">Fruta</option>
              <option value="dairy">Laticínio</option>
              <option value="grains">Grãos</option>
              <option value="snacks">Lanches</option>
              <option value="beverages">Bebidas</option>
              <option value="other">Outro</option>
            </select>
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <label className="ns-field">
              <span>Porção</span>
              <input
                type="number"
                className="ns-input"
                value={newFood.servingSize || 100}
                onChange={(e) => setNewFood({ ...newFood, servingSize: Number(e.target.value) })}
                min="0"
              />
            </label>
            <label className="ns-field">
              <span>Unidade</span>
              <select
                className="ns-input"
                value={newFood.servingUnit || 'g'}
                onChange={(e) => setNewFood({ ...newFood, servingUnit: e.target.value as FoodItem['servingUnit'] })}
              >
                <option value="g">g</option>
                <option value="ml">ml</option>
                <option value="unit">Unidade</option>
                <option value="cup">Xícara</option>
                <option value="tablespoon">Colher de Sopa</option>
                <option value="teaspoon">Colher de Chá</option>
              </select>
            </label>
          </div>

          <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
            <strong style={{ fontSize: '13px', marginBottom: '8px', display: 'block' }}>Informações Nutricionais (por {newFood.servingSize || 100} {newFood.servingUnit || 'g'})</strong>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>Calorias (kcal)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={newFood.nutritionalInfo?.calories || 0}
                  onChange={(e) => setNewFood({
                    ...newFood,
                    nutritionalInfo: {
                      ...newFood.nutritionalInfo!,
                      calories: Number(e.target.value),
                    },
                  })}
                  min="0"
                />
              </label>
              <label className="ns-field">
                <span>Proteínas (g)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={newFood.nutritionalInfo?.protein || 0}
                  onChange={(e) => setNewFood({
                    ...newFood,
                    nutritionalInfo: {
                      ...newFood.nutritionalInfo!,
                      protein: Number(e.target.value),
                    },
                  })}
                  min="0"
                  step="0.1"
                />
              </label>
              <label className="ns-field">
                <span>Carboidratos (g)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={newFood.nutritionalInfo?.carbs || 0}
                  onChange={(e) => setNewFood({
                    ...newFood,
                    nutritionalInfo: {
                      ...newFood.nutritionalInfo!,
                      carbs: Number(e.target.value),
                    },
                  })}
                  min="0"
                  step="0.1"
                />
              </label>
              <label className="ns-field">
                <span>Gorduras (g)</span>
                <input
                  type="number"
                  className="ns-input"
                  value={newFood.nutritionalInfo?.fats || 0}
                  onChange={(e) => setNewFood({
                    ...newFood,
                    nutritionalInfo: {
                      ...newFood.nutritionalInfo!,
                      fats: Number(e.target.value),
                    },
                  })}
                  min="0"
                  step="0.1"
                />
              </label>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

